/* global globalThis */

const defaultRoutePublic = '/front-pmd';

interface RuntimeEnv {
  [key: string]: string | undefined;
}

interface RuntimeProcess {
  env?: RuntimeEnv;
}

function getRuntimeProcess(runtimeProcess?: RuntimeProcess): RuntimeProcess {
  if (runtimeProcess) {
    return runtimeProcess;
  }

  if (!globalThis.process) {
    globalThis.process = {} as typeof globalThis.process;
  }

  return globalThis.process as RuntimeProcess;
}

function ensureProcessEnv(runtimeProcess?: RuntimeProcess): RuntimeEnv {
  const currentProcess = getRuntimeProcess(runtimeProcess);

  if (!currentProcess.env) {
    currentProcess.env = {};
  }

  if (!currentProcess.env.NODE_ENV) {
    currentProcess.env.NODE_ENV = 'development';
  }

  if (!currentProcess.env.ROUTE_PUBLIC) {
    currentProcess.env.ROUTE_PUBLIC = defaultRoutePublic;
  }

  return currentProcess.env;
}

const runtimeEnv = ensureProcessEnv(globalThis.process);
const routePublic = runtimeEnv.ROUTE_PUBLIC;

async function getEnvConfig(): Promise<RuntimeEnv> {
  const response = await fetch(`${routePublic}/init`, {
    headers: {
      Accept: 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to load env config (${response.status})`);
  }

  return response.json();
}

function applyRuntimeEnv(envConfig: RuntimeEnv): void {
  const nextEnv = {
    ...envConfig,
    ROUTE_PUBLIC: envConfig?.ROUTE_PUBLIC || routePublic,
    NODE_ENV: envConfig?.NODE_ENV || runtimeEnv.NODE_ENV, // Need by Vite
  };

  Object.assign(runtimeEnv, nextEnv);
}

async function bootstrapApp(): Promise<void> {
  let envConfig: RuntimeEnv;
  let isEnvServerDown = false;

  try {
    envConfig = await getEnvConfig();
  } catch {
    isEnvServerDown = true;
  }

  applyRuntimeEnv(envConfig);
  runtimeEnv.ENV_SERVER_DOWN = isEnvServerDown ? 'true' : 'false';
  await import('./app');
}

if (typeof jest === 'undefined') {
  bootstrapApp();
}

export {
  applyRuntimeEnv,
  bootstrapApp,
  ensureProcessEnv,
  getEnvConfig,
  getRuntimeProcess,
};
